import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { StravaConnectButton } from "@/components/strava-connect-button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  useDisconnectStrava,
  useListStravaApps,
  useUpdateUserMe,
} from "@/api/orval";
import { useToast } from "@/hooks/use-toast";

export const Settings = () => {
  const { currentUser } = useAuth();
  const { data: stravaApp } = useListStravaApps();
  const updateUsername = useUpdateUserMe();
  const updateUser = useUpdateUserMe();
  const disconnectStrava = useDisconnectStrava();
  const { toast } = useToast();
  const [username, setUsername] = useState(currentUser?.username || "");
  const [usernameError, setUsernameError] = useState("");

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    setUsernameError("");
  };

  const handleUpdateUsername = async () => {
    if (!username) {
      setUsernameError("Username cannot be empty");
      return;
    }

    if (username === currentUser?.username) {
      return;
    }

    try {
      await updateUsername.mutateAsync({ data: { username } });
    } catch (error: any) {
      setUsernameError(
        error.response?.data?.error || "Failed to update username",
      );
      setUsername(currentUser?.username || "");
    }
  };

  const handleToggleUpdateStravaDescription = (checked: boolean) => {
    updateUser.mutate({ data: { update_strava_description: checked } });
  };

  const handleDisconnectStrava = async () => {
    try {
      await disconnectStrava.mutateAsync();
      toast({
        title: "Disconnected from Strava",
        description: "We'll stop importing your activities.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Couldn't disconnect from Strava",
        description: error?.message ?? "Please try again.",
      });
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-8 p-8">
      <div>
        <h1 className="text-2xl font-medium text-gray-900">Settings</h1>
      </div>

      <div className="space-y-6">
        <div>
          <h2 className="mb-6 text-sm font-medium text-gray-500">Profile</h2>
          <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div>
              <Label
                htmlFor="username"
                className="text-xs font-medium text-gray-500"
              >
                Username
              </Label>
              <Input
                id="username"
                value={username}
                onChange={handleUsernameChange}
                onBlur={handleUpdateUsername}
                className="mt-1.5"
                placeholder="Nickname or first name"
              />
              {usernameError && (
                <p className="mt-1.5 text-xs text-red-500">{usernameError}</p>
              )}
              <p className="mt-1.5 text-xs text-gray-500">
                This is your public username that will be visible to others.
              </p>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-6">
            <h2 className="text-sm font-medium text-gray-500">
              Connected accounts
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Connect your Strava account to import your activities
            </p>
          </div>

          <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#fc5200]">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 text-white">
                    <path
                      fill="currentColor"
                      d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h3.066"
                    />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">Strava</span>
                  <p className="text-sm text-gray-500">
                    Import your activities from Strava
                  </p>
                </div>
              </div>
              {stravaApp && stravaApp.length > 0 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex h-8 items-center gap-2 px-3 hover:bg-gray-100"
                    >
                      <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                      <span className="text-sm text-gray-500">Connected</span>
                      <ChevronDown className="h-3 w-3 text-gray-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-red-600 focus:bg-red-50 focus:text-red-600"
                      onClick={handleDisconnectStrava}
                    >
                      Disconnect
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <StravaConnectButton />
              )}
            </div>

            {currentUser?.is_staff && stravaApp && stravaApp.length > 0 && (
              <div className="flex items-start gap-3 border-t border-gray-100 pt-4">
                <Checkbox
                  id="update-strava-description"
                  checked={currentUser?.update_strava_description}
                  onCheckedChange={(checked) =>
                    handleToggleUpdateStravaDescription(checked === true)
                  }
                  className="mt-0.5"
                />
                <div className="flex flex-col">
                  <Label
                    htmlFor="update-strava-description"
                    className="font-medium text-gray-900"
                  >
                    Update activity description on Strava
                  </Label>
                  <p className="text-sm text-gray-500">
                    Add your progress to the description of activities we
                    import.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
